import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  HostBinding,
  Injector,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { UserService } from '../../../services/user/user.service';
import { UserGroupDTO } from '../../../dto/user-group/user-group.dto';
import {
  DunaUserGroupsMembersDialogComponent,
  DUNA_USER_GROUPS_MEMBERS_DIALOG_CONFIG,
} from '../duna-user-groups/duna-user-groups-members-dialog.component';
import {
  DunaUserGroupsViewMembersDialogComponent,
  DUNA_USER_GROUPS_VIEW_MEMBERS_DIALOG_CONFIG,
} from '../duna-user-groups/duna-user-groups-view-members-dialog.component';
import { resolveLocationGuid } from '../../../services/component-data-source/data/groups/duna_user_groups_location';
import { userCanViewUserGroupsLocation } from './user-groups-location-access.utils';
import { filterUserGroupsForLocationDisplay } from './user-groups-location-display.util';
import { fetchLocationUserGroupsWithSettings } from './user-groups-location-fetch.util';

@Component({
  selector: 'app-duna-user-groups-location',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    DynamicTableComponent,
  ],
  templateUrl: './duna-user-groups-location.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-user-groups-location.component.scss',
})
export class DunaUserGroupsLocationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  locationGuid: string | null = null;
  accessChecked = false;
  componentVisible = false;

  summary = { groupCount: 0, memberTotal: 0, avgStatus: 0 };

  private viewListener = (e: Event) => this.handleViewMembers(e as CustomEvent);
  private manageListener = (e: Event) =>
    this.handleManageMembers(e as CustomEvent);

  @ViewChild('groupsTable') groupsTable?: DynamicTableComponent;

  readonly dynamicInput = {
    dataSource: { type: 'text', value: 'duna_user_groups_location' },
    tableTitle: { type: 'text', value: 'Brugergrupperinger' },
    tableDescription: {
      type: 'textarea',
      value:
        'Tildel lokationens brugere til grupperinger og følg vedligeholdelsesstatistik.',
    },
  };

  @HostBinding('style.display')
  get hostDisplay(): string {
    if (!this.accessChecked) return 'none';
    return this.componentVisible ? 'block' : 'none';
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private componentDataSourceService: ComponentDataSourceService,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    this.locationGuid = resolveLocationGuid(this.router);
    window.addEventListener(
      'duna-user-groups-location-view-members',
      this.viewListener as EventListener
    );
    window.addEventListener(
      'duna-user-groups-location-manage-members',
      this.manageListener as EventListener
    );
    void this.checkAccess();
  }

  private async waitForAuthReady(maxMs = 5000): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < maxMs) {
      if (this.componentDataSourceService.authService.getCurrentUser()) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  private async checkAccess(): Promise<void> {
    await this.waitForAuthReady();
    const authService = this.componentDataSourceService.authService;
    const userRoles = authService.getUserRoles();
    let userLocationGuids: string[] = [];
    try {
      const userService = this.injector.get(UserService);
      const locations = await firstValueFrom(
        userService.getUserLocationsFromApi()
      );
      userLocationGuids = (locations ?? [])
        .map((loc) => loc.guid)
        .filter(Boolean);
    } catch {
      userLocationGuids = [];
    }
    const roleAccess = userCanViewUserGroupsLocation({
      userRoles,
      locationGuid: this.locationGuid,
      userLocationGuids,
    });
    if (!roleAccess || !this.locationGuid) {
      this.componentVisible = false;
      this.accessChecked = true;
      this.cdr.markForCheck();
      return;
    }
    await this.resolveSectionVisibility();
    this.accessChecked = true;
    this.cdr.markForCheck();
  }

  private async resolveSectionVisibility(): Promise<void> {
    if (!this.locationGuid) {
      this.componentVisible = false;
      return;
    }
    const { hideEmptyAtLocation, groups } =
      await fetchLocationUserGroupsWithSettings(
        this.componentDataSourceService,
        this.locationGuid
      );
    const visibleGroups = filterUserGroupsForLocationDisplay(
      groups,
      hideEmptyAtLocation
    );
    this.componentVisible = visibleGroups.length > 0;
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'duna-user-groups-location-view-members',
      this.viewListener as EventListener
    );
    window.removeEventListener(
      'duna-user-groups-location-manage-members',
      this.manageListener as EventListener
    );
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleViewMembers(event: CustomEvent): void {
    this.dialog.open(DunaUserGroupsViewMembersDialogComponent, {
      ...DUNA_USER_GROUPS_VIEW_MEMBERS_DIALOG_CONFIG,
      data: {
        group: event.detail as UserGroupDTO,
        locationGuid: this.locationGuid,
      },
    });
  }

  private handleManageMembers(event: CustomEvent): void {
    this.dialog
      .open(DunaUserGroupsMembersDialogComponent, {
        ...DUNA_USER_GROUPS_MEMBERS_DIALOG_CONFIG,
        data: {
          group: event.detail as UserGroupDTO,
          locationGuid: this.locationGuid,
        },
      })
      .afterClosed()
      .subscribe((ok) => {
        if (ok) void this.refresh();
      });
  }

  private async refresh(): Promise<void> {
    await this.resolveSectionVisibility();
    this.groupsTable?.refreshTable();
    this.componentDataSourceService.refreshData();
    this.cdr.detectChanges();
  }
}
